import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./twin-map.css";
import "./twin-map-scope.css";
import "./twin-marker.css";
import "./location-geocode.css";
import { shouldOpenPublicTwin } from "./twin-map-access.js";

const FALLBACK_STYLE = {
  version: 8,
  sources: { osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, attribution: "© OpenStreetMap contributors" } },
  layers: [{ id: "osm", type: "raster", source: "osm", paint: { "raster-saturation": -0.85, "raster-brightness-max": 0.32, "raster-contrast": 0.25, "raster-opacity": 0.78 } }],
};

export function TwinMap({ session, mapStyleUrl, revision = 0, onOpenTwin, onLogin }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const dataRef = useRef({ type: "FeatureCollection", features: [], unlocated: [] });
  const markersRef = useRef([]);
  const fittedEndpointRef = useRef("");
  const sessionRef = useRef(session);
  const openTwinRef = useRef(onOpenTwin);
  sessionRef.current = session;
  openTwinRef.current = onOpenTwin;
  const [data, setData] = useState({ type: "FeatureCollection", features: [], unlocated: [] });
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const endpoint = session ? `/api/my/map${onlyMine ? "?scope=mine" : ""}` : "/api/public/map";

  async function reload() {
    const response = await fetch(endpoint, { cache: "no-store" });
    if (!response.ok) throw new Error(`Map API ${response.status}`);
    setData(await response.json());
  }

  useEffect(() => { if (!session) setOnlyMine(false); }, [session]);
  useEffect(() => {
    let active = true;
    const refresh = () => reload().catch((error) => active && setMessage(error.message));
    fittedEndpointRef.current = "";
    void refresh();
    const timer = setInterval(refresh, 5_000);
    return () => { active = false; clearInterval(timer); };
  }, [endpoint, revision]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({ container: containerRef.current, style: mapStyleUrl || FALLBACK_STYLE, center: [10, 34], zoom: 1.7, minZoom: 1.2 });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.on("load", () => {
      renderTwinMarkers(map, dataRef.current.features, markersRef, focusTwin, openTwin);
    });
    const refreshMarkers = () => renderTwinMarkers(map, dataRef.current.features, markersRef, focusTwin, openTwin);
    map.on("moveend", refreshMarkers);
    return () => { markersRef.current.forEach((marker) => marker.remove()); markersRef.current = []; map.remove(); mapRef.current = null; };
  }, [mapStyleUrl]);

  useEffect(() => {
    dataRef.current = data;
    const map = mapRef.current;
    if (!map) return;
    const update = () => {
      renderTwinMarkers(map, data.features, markersRef, focusTwin, openTwin);
      if (fittedEndpointRef.current === endpoint) return;
      fittedEndpointRef.current = endpoint;
      if (data.features.length === 1) map.easeTo({ center: data.features[0].geometry.coordinates, zoom: 5 });
      else if (data.features.length > 1) {
        const bounds = data.features.reduce((value, feature) => value.extend(feature.geometry.coordinates), new maplibregl.LngLatBounds(data.features[0].geometry.coordinates, data.features[0].geometry.coordinates));
        map.fitBounds(bounds, { padding: 90, maxZoom: 10, duration: 700 });
      }
    };
    if (map.isStyleLoaded()) update(); else map.once("load", update);
  }, [data, endpoint]);

  function focusTwin(twin) {
    setSelected(twin);
    if (!twin.coordinates || !mapRef.current) return;
    const map = mapRef.current;
    map.easeTo({ center: twin.coordinates, zoom: Math.max(map.getZoom(), 6), duration: 900, essential: true });
  }

  function openTwin(twin) {
    openTwinRef.current(twin.twinId, shouldOpenPublicTwin(twin, Boolean(sessionRef.current)));
  }

  const all = [...data.features.map((feature) => ({ ...feature.properties, coordinates: feature.geometry.coordinates })), ...data.unlocated];
  const summary = summarizeOperationalStatus(all);
  return <section className="twin-map-view">
    <div className="map-heading"><div><span>{session ? "AUTHENTICATED TWIN MAP" : "PUBLIC TWIN MAP"}</span><h1>{session ? "Your Digital Twin Map" : "Explore Digital Twins"}</h1><p>{session ? onlyMine ? "Only Twins associated with your DID are shown, including public and private Twins." : "Your workspace and the public Twin network. Private coordinates are visible only in this DID session." : "Public and demonstration Twins. Zoom or select a marker to inspect one."}</p></div><div className="map-heading-actions">{session && <label className="map-scope-toggle"><input type="checkbox" checked={onlyMine} onChange={(event) => { setOnlyMine(event.target.checked); setSelected(null); }} /><i aria-hidden="true" /><span><b>ONLY MY TWINS</b><small>HIDE PUBLIC TWINS OWNED BY OTHERS</small></span></label>}<strong>{all.length.toString().padStart(2, "0")} TWINS</strong></div></div>
    {message && <div className="map-message">{message}</div>}
    <div className="map-layout">
      <div className="map-stage">
        <div className="map-canvas" ref={containerRef} aria-label="Interactive Digital Twin map" />
        <MapSummary summary={summary} />
      </div>
      <aside className="map-sidebar">
        <header><span>MAP INDEX</span><small>{data.features.length} LOCATED · {data.unlocated.length} UNLOCATED</small></header>
        {all.map((twin) => <article className={selected?.twinId === twin.twinId ? "active" : ""} key={twin.twinId} onClick={() => focusTwin(twin)} onDoubleClick={() => openTwin(twin)}>
          <small>{twin.livePosition ? twin.locationVisibility === "public-live" ? "PUBLIC LIVE POSITION" : "PRIVATE LIVE POSITION" : twin.isDemo ? "PUBLIC DEMONSTRATION" : twin.locationVisibility === "private" ? "PRIVATE LOCATION" : twin.locationVisibility === "public" ? "PUBLIC LOCATION" : "LOCATION NOT SET"}</small>
          <strong>{twin.name}</strong>{twin.livePosition ? <em>{positionSummary(twin)}</em> : twin.locationLabel && <em>{twin.locationLabel}</em>}<code>{shortId(twin.twinId)}</code>
          <div><button type="button" onClick={(event) => { event.stopPropagation(); openTwin(twin); }}>OPEN</button></div>
        </article>)}
        {!all.length && <p className="map-empty">No Twins are available in this map.</p>}
        {!session && <button className="map-login" type="button" onClick={onLogin}>DID LOGIN / SHOW MY TWINS</button>}
      </aside>
    </div>
  </section>;
}

function MapSummary({ summary }) {
  return <aside className="map-summary" aria-label="Digital Twin operational summary">
    <header><span>FLEET SUMMARY</span><small>REALTIME STATUS</small></header>
    <div className="map-summary-total"><strong>{String(summary.total).padStart(2, "0")}</strong><span>TOTAL TWINS</span></div>
    <dl>
      <div className="ok"><dt><i />OK</dt><dd>{summary.ok}</dd></div>
      <div className="degraded"><dt><i />DEGRADED</dt><dd>{summary.degraded}</dd></div>
      <div className="offline"><dt><i />OFFLINE</dt><dd>{summary.offline}</dd></div>
      <div className="unmonitored"><dt><i />NOT MONITORED</dt><dd>{summary.unmonitored}</dd></div>
    </dl>
  </aside>;
}

function summarizeOperationalStatus(twins) {
  return twins.reduce((summary, twin) => {
    const state = ["ok", "degraded", "offline"].includes(twin.operationalStatus) ? twin.operationalStatus : "unmonitored";
    summary.total += 1;
    summary[state] += 1;
    return summary;
  }, { total: 0, ok: 0, degraded: 0, offline: 0, unmonitored: 0 });
}

function statusLabel(status) {
  return ({ ok: "OK", degraded: "DEGRADED", offline: "OFFLINE", unmonitored: "NOT MONITORED" })[status] ?? "NOT MONITORED";
}

function shortId(value) { return value ? `${value.slice(0, 10)}…${value.slice(-8)}` : "—"; }

function renderTwinMarkers(map, features, markerRef, focus, open) {
  markerRef.current.forEach((marker) => marker.remove());
  markerRef.current = [];
  const groups = new Map();
  for (const feature of features) {
    const point = map.project(feature.geometry.coordinates);
    const key = `${Math.floor(point.x / 64)}:${Math.floor(point.y / 64)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(feature);
  }
  for (const group of groups.values()) {
    const coordinates = [group.reduce((sum, item) => sum + item.geometry.coordinates[0], 0) / group.length, group.reduce((sum, item) => sum + item.geometry.coordinates[1], 0) / group.length];
    const element = document.createElement("button");
    element.type = "button";
    const live = group.length === 1 && group[0].properties.livePosition;
    element.className = `twin-map-marker${group.length > 1 ? " cluster" : group[0].properties.isDemo ? " demo" : ""}${live ? " live" : ""}`;
    if (live && Number.isFinite(Number(group[0].properties.headingDegrees))) element.style.setProperty("--heading", `${Number(group[0].properties.headingDegrees)}deg`);
    element.textContent = group.length > 1 ? String(group.length) : "";
    element.title = group.length > 1 ? `${group.length} Digital Twins` : group[0].properties.name;
    const twin = group.length === 1 ? { ...group[0].properties, coordinates: group[0].geometry.coordinates } : null;
    let singleClickTimer;
    element.addEventListener("click", (event) => {
      event.preventDefault(); event.stopPropagation();
      if (group.length > 1) return map.easeTo({ center: coordinates, zoom: Math.min(map.getZoom() + 2, 15) });
      clearTimeout(singleClickTimer);
      singleClickTimer = setTimeout(() => focus(twin), 260);
    });
    element.addEventListener("dblclick", (event) => {
      event.preventDefault(); event.stopPropagation(); clearTimeout(singleClickTimer);
      if (twin) open(twin);
    });
    markerRef.current.push(new maplibregl.Marker({ element }).setLngLat(coordinates).addTo(map));
  }
}

function positionSummary(twin) {
  const parts = [];
  if (Number.isFinite(Number(twin.speedKph))) parts.push(`${Number(twin.speedKph).toFixed(1)} km/h`);
  if (Number.isFinite(Number(twin.headingDegrees))) parts.push(`${Number(twin.headingDegrees).toFixed(0)}°`);
  const age = Date.now() - Number(twin.positionObservedAt ?? 0);
  if (Number.isFinite(age) && age >= 0) parts.push(age < 60_000 ? `${Math.max(0, Math.round(age / 1000))}s ago` : `${Math.round(age / 60_000)}m ago`);
  return parts.join(" · ") || "Live position";
}
